import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { UserPlus } from "lucide-react";
import { InviteService } from "@/features/Users/services/inviteService";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useToast } from "@/shared/hooks/use-toast";
import { Spinner } from "@/shared/components/ui/spinner";
import { useEffect, useCallback } from "react";
import { InviteUserDialog } from "./InviteUserDialog";
import { UsersTable } from "./UsersTable";
import { OrganizationUsersTable } from "@/features/Organization/components/OrganizationUsersTable";
import { EditUserWorkspacesDialog } from "./EditUserWorkspacesDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { UsersSearchBar } from "./UsersSearchBar";
import {
  UserService,
  User,
  Workspace,
} from "@/features/Users/services/userService";
import { WorkspaceService } from "@/features/Workspaces/services/workspaceService";
import { analyticsService } from "@/shared/services/analytics";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { UserRole } from "@/shared/types/user-role";

// Using User and Product interfaces from UserService

export const UserSettings = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { isSuperAdmin: currentUserIsSuperAdmin } = useIsSuperAdminRole();
  const { selectedOrganization } = useSuperAdminOrg();
  const orgKeySegment = currentUserIsSuperAdmin
    ? selectedOrganization?.id
    : undefined;
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("organization-users");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [organizationId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  // Buscar convites usando o novo InviteService em vez do acesso direto ao Supabase
  const { data: invites, isLoading: isLoadingInvites } = useQuery({
    queryKey: ["invites", organizationId, orgKeySegment],
    queryFn: async () => {
      try {
        // Filtrar por organização quando disponível
        const filters = organizationId
          ? { organization_id: organizationId }
          : undefined;
        const invitesData = await InviteService.listInvites(filters);
        return invitesData;
      } catch (error) {
        console.error("Erro ao buscar convites:", error);
        throw error;
      }
    },
    enabled: true, // Sempre buscar convites inicialmente, refinar depois que tivermos o organization_id
  });

  // Buscar produtos da organização usando JWT
  const { data: organizationProducts = [] } = useQuery<Workspace[]>({
    queryKey: ["organization-products", orgKeySegment],
    queryFn: async () => {
      try {
        const products =
          await WorkspaceService.getCurrentOrganizationProducts();
        return products;
      } catch (error) {
        console.error("Erro ao buscar produtos da organização:", error);
        throw error;
      }
    },
    // Catálogo de workspaces só é usado pelos diálogos de convite/edição — busca lazy
    // ao abrir um deles, em vez de no load da tela (evita 1 request na aba de convites).
    enabled: dialogOpen || editDialogOpen,
    staleTime: 60000, // 1 minuto
    retry: 1,
  });

  // Buscar usuários da organização usando JWT
  const {
    data: organizationUsers = [],
    isLoading: isLoadingOrgUsers,
    error: orgUsersError,
  } = useQuery<User[]>({
    queryKey: ["organization-users", orgKeySegment],
    queryFn: async () => {

      try {
        // Usar o UserService para obter usuários da organização atual
        const users = await UserService.getCurrentOrganizationUsers();

        // Adicionar flag de loading para produtos
        return users.map((user) => ({
          ...user,
          productsLoading: true,
        }));
      } catch (error) {
        console.error("[UserSettings] Erro ao buscar usuários:", error);
        throw error;
      }
    },
    staleTime: 60000, // 1 minuto
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const {
    data: searchResults,
    isLoading: isSearchingUsers,
    error: searchUsersError,
  } = useQuery({
    queryKey: ["organization-users-search", orgKeySegment, searchTerm],
    queryFn: async () => {
      if (!searchTerm) return null;
      const response = await UserService.searchCurrentOrganizationUsers(
        searchTerm,
        { limit: 50 },
      );
      analyticsService.trackEvent("users_searched", {
        scope: "organization",
        query_length: searchTerm.length,
        results_count: response.users.length,
      });
      return response;
    },
    enabled: !!searchTerm,
  });

  // When a search is active, the displayed list comes from the search endpoint;
  // otherwise from the full org list. Os produtos vêm embutidos na resposta da
  // lista — não há mais uma busca por usuário exibido.
  const baseUsers: User[] = searchTerm
    ? (searchResults?.users ?? []).map((user) => ({
        ...user,
        productsLoading: true,
      }))
    : organizationUsers;

  // Mutação para deletar um usuário administrativamente (remove auth + inativa profile + remove product_users)
  const removeFromOrganizationMutation = useMutation({
    mutationFn: (userId: string) =>
      UserService.removeUserFromOrganization(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization-users", orgKeySegment],
      });
      // Match parcial: invalida a busca em todas as variantes de searchTerm —
      // com busca ativa a lista exibida vem dela, não da lista completa.
      queryClient.invalidateQueries({
        queryKey: ["organization-users-search", orgKeySegment],
      });
      // Prefixo, como nos outros pontos que mexem em vínculo de produto
      // (`AddWorkspaceUsersDialog`, `WorkspaceDialog`): esta tela não lê essa
      // query, mas o `WorkspaceContext` lê, e a chave dele inclui a organização.
      // Invalidar pelo prefixo alcança quem estiver montado; a chave com o id do
      // usuário removido só casava quando ele era o próprio usuário logado.
      queryClient.invalidateQueries({ queryKey: ["user-products"] });
      setDeleteDialogOpen(false);
      toast({
        title: t("settings.users.removeFromOrgSuccess"),
        description: t("settings.users.removeFromOrgSuccessDescription"),
        duration: 5000,
      });
      if (selectedUser) {
        analyticsService.trackEvent("user_removed", {
          user_email: selectedUser.email,
          removed_by: user?.email || "unknown",
        });
      }
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("settings.users.removeFromOrgError"),
        description: t("settings.users.removeFromOrgErrorDescription"),
      });
    },
  });

  // Os produtos de cada usuário já vêm embutidos na resposta da lista.
  const allUsersWithProducts = baseUsers.map((user) => ({
    ...user,
    products: user.products ?? [],
    productsLoading: false,
  }));

  // Ordenar: regulares primeiro, superadmins por último
  const usersWithProducts = [
    ...allUsersWithProducts.filter((u) => u.role !== UserRole.SUPERADMIN),
    ...allUsersWithProducts.filter((u) => u.role === UserRole.SUPERADMIN),
  ];

  const isPendingCount = invites?.length || 0;

  useEffect(() => {
    const handleToastEvent = (event: CustomEvent) => {
      if (event.detail) {
        toast({
          title: event.detail.title,
          description: event.detail.description,
          variant: event.detail.variant || "default",
        });
      }
    };

    // Adicionar o event listener com type assertion para CustomEvent
    window.addEventListener("show-toast", handleToastEvent as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener(
        "show-toast",
        handleToastEvent as EventListener,
      );
    };
  }, [toast]);

  const handleEditUser = useCallback(
    (user: User) => {
      // Garantir que temos dados válidos
      if (!user || !user.id) {
        console.error(
          "[ERROR] Tentativa de editar usuário com dados inválidos",
          user,
        );
        toast({
          variant: "destructive",
          title: t("common.error"),
          description: t("settings.users.invalidUserData"),
        });
        return;
      }

      // Abordagem direta e simplificada
      setSelectedUser(user);
      setEditDialogOpen(true);
    },
    [toast, t],
  );

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedUser) {
      removeFromOrganizationMutation.mutate(selectedUser.id);
    }
  };

  const handleSearchSubmit = (term: string) => {
    setSearchTerm(term);
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setSearchTerm(null);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-end space-y-0 pb-4">
        <Button onClick={() => setDialogOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          {t("users.inviteTitle")}
        </Button>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="organization-users">
              {t("users.activeUsersTab")}
            </TabsTrigger>
            <TabsTrigger
              value="pending-invites"
              className="flex items-center gap-2"
            >
              {t("users.pendingInvitesTab")}
              {isPendingCount > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/15 px-1.5 text-xs font-medium text-primary">
                  {isPendingCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="pending-invites" className="m-0">
              {isLoadingInvites ? (
                <div className="flex flex-col items-center justify-center p-8 space-y-4">
                  <Spinner className="h-8 w-8" />
                  <p className="text-sm text-muted-foreground">
                    {t("common.loading")}
                  </p>
                </div>
              ) : invites && invites.length > 0 ? (
                <UsersTable invites={invites} />
              ) : (
                <div className="p-4 border rounded-md">
                  <p className="text-sm text-muted-foreground">
                    {t("users.noInvites")}
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="organization-users" className="m-0">
              <div className="mb-4">
                <UsersSearchBar
                  value={searchInput}
                  onChange={setSearchInput}
                  onSubmit={handleSearchSubmit}
                  onClear={handleSearchClear}
                  isSearching={isSearchingUsers}
                  isActive={!!searchTerm}
                />
              </div>

              {isLoadingOrgUsers && !searchTerm ? (
                <div className="flex flex-col items-center justify-center p-8 space-y-4">
                  <Spinner className="h-8 w-8" />
                  <p className="text-sm text-muted-foreground">
                    {t("common.loading")}
                  </p>
                </div>
              ) : isSearchingUsers ? (
                <div className="flex flex-col items-center justify-center p-8 space-y-4">
                  <Spinner className="h-8 w-8" />
                  <p className="text-sm text-muted-foreground">
                    {t("common.loading")}
                  </p>
                </div>
              ) : orgUsersError && !searchTerm ? (
                <div className="p-4 border rounded-md bg-destructive/10">
                  <p className="text-sm text-destructive">
                    {t("common.errorLoadingData")}
                  </p>
                </div>
              ) : searchUsersError ? (
                <div className="p-4 border rounded-md bg-destructive/10">
                  <p className="text-sm text-destructive">
                    {t("common.errorLoadingData")}
                  </p>
                </div>
              ) : usersWithProducts.length > 0 ? (
                <OrganizationUsersTable
                  users={usersWithProducts}
                  onEditUser={handleEditUser}
                  onDeleteUser={handleDeleteUser}
                  currentUserId={user?.id}
                />
              ) : searchTerm ? (
                <div className="p-4 border rounded-md">
                  <p className="text-sm text-muted-foreground">
                    {t("settings.users.search.noResults")}
                  </p>
                </div>
              ) : (
                <div className="p-4 border rounded-md">
                  <p className="text-sm text-muted-foreground">
                    {t("users.noOrganizationUsers")}
                  </p>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </CardContent>

      {/* Dialogs */}
      <InviteUserDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setActiveTab("pending-invites");
        }}
        products={organizationProducts}
        organizationId={organizationId || undefined}
      />

      <EditUserWorkspacesDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        user={selectedUser}
        organizationProducts={organizationProducts}
      />

      <DeleteUserDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        user={selectedUser}
        onConfirm={handleConfirmDelete}
        isDeleting={removeFromOrganizationMutation.isPending}
        mode="organization"
      />
    </Card>
  );
};
